import { FeatureFlag } from '@revelio/core';
import {
  COMPOSITION_GET_DATA_V3,
  type CompositionV3DataQuery,
  type CompositionV3DataQueryVariables,
  Dimension1,
  EntKind,
  GET_SCREENER_DATA,
  type GetSentimentDataQuery,
  type GetSentimentDataQueryVariables,
  POSTING_SOURCE_IDS,
  POSTINGS_GET_ACTIVE,
  POSTINGS_V3_GET_DATA,
  type PostingsGetActiveQuery,
  type PostingsGetActiveQueryVariables,
  type PostingsV3DataQuery,
  type PostingsV3Ent,
  type ScreenerReq,
  type ScreenerRespEntity,
  ScreenerSortCol,
  SENTIMENT_GET_OVERTIME_DATA_V2,
} from '@revelio/data-access';

import { useUnleashFlag } from '../../../../../shared/unleash-flags/use-unleash-flag';
import { findFilterById } from '../../../filters/engine/filterRegistry';
import { useEffectiveFilters } from '../../../filters/hooks/useEffectiveFilters';
import {
  FilterId,
  FilterType,
  type PrimaryFilterId,
} from '../../../filters/types/filters';
import { getCompositionV3Variables } from '../../../filters/utils/composition-v3/getCompositionV3Variables';
import { buildScreenerFilters } from '../../../filters/utils/get-screener-filters';
import { getCustomRoleFilter } from '../../../filters/utils/getCustomRoleFilter';
import { getListFilter } from '../../../filters/utils/getListFilter';
import { buildPostingsV3VariablesFromEntities } from '../../../filters/utils/getPostingsV3Variables';
import { getV2GeographyFilters } from '../../../filters/utils/getV2GeographyFilters';
import { useChart } from '../../Chart/ChartProvider';
import { useTabContentConfig } from '../../PageConfigProvider/PageConfigProvider';
import { PlotFilterType } from '../../PageConfigProvider/types/item';
import {
  isPostingMetric,
  isScreenerMetric,
  isSentimentMetric,
  type MetricValues,
  type PostingMetricValues,
  type ScreenerMetricValues,
  type SentimentMetricValues,
} from '../../PageConfigProvider/types/metric';
import type { MultiMetricProviderResult } from '../MultiMetricProvider';
import {
  type DataProviderChildren,
  DataProviderQueryStatus,
  DataType,
} from '../types';
import {
  combineQueryStatus,
  useUrqlQueryWithStatus,
} from '../utils/data-provider-results-utils';
import { processMultipleScreenerMetrics } from '../utils/screener-metrics-utils';
import { usePostingsV3WeekRefs } from '../utils/usePostingsV3WeekRefs';
import { useSnowflakeSavedQuery } from '../utils/useSnowflakeSavedQuery';
import { convertPostingsV3ToActivePostings } from './convert-postings-v3-to-market-metrics';

const DEFAULT_DISCOVERY_LIMIT = 10;

type EffectiveFilter = ReturnType<typeof useEffectiveFilters>[number];

const buildSecondaryFilters = (effectiveFilters: EffectiveFilter[]) => {
  const roleFilter = findFilterById(effectiveFilters, FilterId.ROLE);
  const geoFilter = findFilterById(effectiveFilters, FilterId.GEOGRAPHY);
  const seniorityFilter = findFilterById(effectiveFilters, FilterId.SENIORITY);
  const geo = geoFilter ? getV2GeographyFilters(geoFilter) : {};

  return {
    ...(roleFilter ? getCustomRoleFilter(roleFilter, effectiveFilters) : {}),
    ...(geo.region ? { region: geo.region } : {}),
    ...(geo.country ? { country: geo.country } : {}),
    ...(geo.msa ? { msa: geo.msa } : {}),
    ...(seniorityFilter?.type === FilterType.LIST
      ? getListFilter(seniorityFilter, 'seniority')
      : {}),
  };
};

const getLatestFromSeries = (
  series: ReadonlyArray<{ value?: number | null } | null> | null | undefined
): number | null => {
  if (!Array.isArray(series) || series.length === 0) return null;
  const last = series[series.length - 1];
  return typeof last?.value === 'number' ? last.value : null;
};

type PostingCategory = NonNullable<
  NonNullable<PostingsGetActiveQuery['posting']>[number]
>['category'];

const getLatestPostingCount = (categories: PostingCategory): number | null => {
  if (!Array.isArray(categories) || categories.length === 0) return null;
  const latest = [...categories]
    .filter((c) => c?.metadata?.shortName)
    .sort((a, b) =>
      (b?.metadata?.shortName ?? '').localeCompare(a?.metadata?.shortName ?? '')
    )[0];
  return typeof latest?.metrics?.active === 'number'
    ? Math.round(latest.metrics.active)
    : null;
};

const extractSentimentMetricValues = (
  sentimentEntities: NonNullable<GetSentimentDataQuery['sentiment']>,
  screenerEntities: ScreenerRespEntity[],
  metric: SentimentMetricValues
): Array<{ dim: string; value: number }> =>
  screenerEntities.flatMap((entity) => {
    const label = entity.long_name || entity.short_name || '';
    const sentimentEntity = sentimentEntities.find(
      (se) => se?.metadata?.id === entity.entity_id
    );

    const scores = sentimentEntity?.result?.scores as
      | Record<string, ReadonlyArray<{ value?: number | null } | null> | null>
      | null
      | undefined;

    const value = getLatestFromSeries(scores?.[metric]);
    if (value === null) return [];

    return [{ dim: label, value: value / 100 }];
  });

const extractActivePostings = (
  postingsEntities: NonNullable<PostingsGetActiveQuery['posting']>,
  screenerEntities: ScreenerRespEntity[]
): Array<{ dim: string; value: number }> =>
  screenerEntities.flatMap((entity) => {
    const label = entity.long_name || entity.short_name || '';
    const posting = postingsEntities.find(
      (p) => p?.metadata?.id === entity.entity_id
    );
    const count = getLatestPostingCount(posting?.category);
    return count !== null ? [{ dim: label, value: count }] : [];
  });

const buildPostingsByRcid = (
  postingsEntities: NonNullable<PostingsGetActiveQuery['posting']>
): Map<number, number> => {
  const map = new Map<number, number>();
  for (const posting of postingsEntities) {
    const rcid = posting?.metadata?.id;
    if (rcid == null) continue;
    const count = getLatestPostingCount(posting?.category);
    if (count !== null) {
      map.set(rcid, count);
    }
  }
  return map;
};

const extractMarketShare = (
  screenerEntities: ScreenerRespEntity[],
  headcountByCompany: Map<string, number>,
  totalHeadcount: number | null
): Array<{ dim: string; value: number }> =>
  screenerEntities.flatMap((entity) => {
    const label = entity.long_name || entity.short_name || '';
    const companyHeadcount = headcountByCompany.get(label) ?? null;
    if (
      companyHeadcount === null ||
      totalHeadcount === null ||
      totalHeadcount <= 0
    ) {
      return [];
    }
    return [{ dim: label, value: companyHeadcount / totalHeadcount }];
  });

type SnowflakeRow = Record<string, unknown>;

const quantile = (values: number[], q: number): number => {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const pos = (sorted.length - 1) * q;
  const base = Math.floor(pos);
  const rest = pos - base;
  const baseVal = sorted[base] ?? 0;
  const nextVal = sorted[base + 1];
  return nextVal !== undefined ? baseVal + rest * (nextVal - baseVal) : baseVal;
};

const findTargetCompanies = (rows: SnowflakeRow[]): string[] => {
  const companies = rows.map((row) => ({
    name: String(row['Company'] ?? ''),
    salary:
      typeof row['Salary'] === 'number' ? (row['Salary'] as number) : null,
    sentiment:
      typeof row['Sentiment Rating'] === 'number'
        ? (row['Sentiment Rating'] as number)
        : null,
    prestige: String(row['Prestige'] ?? ''),
  }));

  const salaries = companies
    .map((c) => c.salary)
    .filter((v): v is number => v !== null);
  const sentiments = companies
    .map((c) => c.sentiment)
    .filter((v): v is number => v !== null);
  const highPrestigeNames = new Set(
    companies
      .filter((c) => c.prestige === 'High' || c.prestige === 'Very High')
      .map((c) => c.name)
  );

  const findAtThreshold = (salaryQ: number, sentimentQ: number) => {
    const salaryThreshold =
      salaries.length > 0 ? quantile(salaries, salaryQ) : 0;
    const sentimentThreshold =
      sentiments.length > 0 ? quantile(sentiments, sentimentQ) : 0;
    return companies
      .filter(
        (c) =>
          c.salary !== null &&
          c.salary < salaryThreshold &&
          c.sentiment !== null &&
          c.sentiment < sentimentThreshold &&
          highPrestigeNames.has(c.name)
      )
      .map((c) => c.name);
  };

  const targets = findAtThreshold(0.33, 0.33);
  return targets.length > 0 ? targets : findAtThreshold(0.5, 0.5);
};

const buildSnowflakeTableRows = (
  snowflakeRows: SnowflakeRow[],
  postingsByRcid: Map<number, number>,
  headcountColumnName: string
) => {
  const rows = snowflakeRows.map((row) => {
    const rcid = row['rcid'];
    return {
      Company: String(row['Company'] ?? ''),
      [headcountColumnName]: row['Headcount'],
      'Market Share':
        typeof row['Market Share'] === 'number'
          ? (row['Market Share'] as number) / 100
          : null,
      Salary: row['Salary'],
      'Active Postings':
        typeof rcid === 'number' ? (postingsByRcid.get(rcid) ?? null) : null,
      'Sentiment Rating': row['Sentiment Rating'],
      prestige: row['Prestige'] ?? '',
    };
  });

  const columnMetrics: Record<string, string> = {
    [headcountColumnName]: 'screener_headcount',
    'Market Share': 'market_share',
    Salary: 'screener_salary',
    'Active Postings': 'postings_active',
    'Sentiment Rating': 'demand_supply',
  };

  const columnFormats: Record<string, string> = {
    'Market Share': '.2%',
  };

  return { rows, columnMetrics, columnFormats };
};

const extractSnowflakeMarketShare = (
  snowflakeRows: SnowflakeRow[]
): Array<{ dim: string; value: number }> =>
  snowflakeRows.flatMap((row) => {
    const dim = String(row['Company'] ?? '');
    const value = row['Market Share'];
    return typeof value === 'number' ? [{ dim, value: value / 100 }] : [];
  });

const getTotalHeadcount = (
  compositionData: CompositionV3DataQuery | undefined
): number | null => {
  const headcountTs =
    compositionData?.composition_v3?.entities?.[0]?.metrics?.headcount;
  if (!Array.isArray(headcountTs) || headcountTs.length === 0) return null;
  const last = headcountTs[headcountTs.length - 1];
  return typeof last?.val === 'number' ? last.val : null;
};

/**
 * Fetches screener data to discover top entities, then optionally chains
 * sentiment, postings, and composition queries for those entities. Returns
 * each requested metric as its own DataType.SINGLE entry in the byMetric
 * map so the standard table pipeline can merge columns.
 */
type CompetitiveLandscapeProviderProps = {
  primaryDimension: PrimaryFilterId;
  metrics: MetricValues[];
  children: DataProviderChildren<MultiMetricProviderResult>;
};

export const CompetitiveLandscapeProvider = ({
  primaryDimension,
  metrics,
  children,
}: CompetitiveLandscapeProviderProps) => {
  const isPostingsV3Enabled = useUnleashFlag(FeatureFlag.PostingsV3);
  const { item } = useChart();
  const { config: tabConfig } = useTabContentConfig();

  // Resolve the Snowflake config for competitive landscape data by finding
  // the table item that shares the same dimensionSource. This lets text
  // charts reuse the table's config without duplicating it.
  const snowflakeConfig = tabConfig.items.find(
    (sibling) =>
      sibling.plot?.dimensionSource === item?.plot?.dimensionSource &&
      sibling.plot?.chartType === 'table' &&
      sibling.plot?.snowflakeConfig
  )?.plot?.snowflakeConfig;
  const effectiveFilters = useEffectiveFilters();
  const compositionEffectiveFilters = useEffectiveFilters({
    viewGranularity: 'month',
  });

  const discoveryLimit =
    item?.plot?.plotFilters?.find((f) => f.type === PlotFilterType.TOP_N)
      ?.value ?? DEFAULT_DISCOVERY_LIMIT;

  const roleFilter = effectiveFilters.find(
    (f) => f.id === FilterId.ROLE && f.type === FilterType.TREE
  );
  const headcountColumnName =
    roleFilter?.type === FilterType.TREE && roleFilter.selections?.[0]?.name
      ? `${roleFilter.selections[0].name} Roles`
      : 'Headcount';

  const screenerMetrics = metrics.filter((m): m is ScreenerMetricValues =>
    isScreenerMetric(m)
  );
  const sentimentMetrics = metrics.filter((m): m is SentimentMetricValues =>
    isSentimentMetric(m)
  );
  const postingMetrics = metrics.filter((m): m is PostingMetricValues =>
    isPostingMetric(m)
  );
  const needsMarketShare = metrics.includes('market_share');

  const secondaryFilters = buildSecondaryFilters(effectiveFilters);

  // Screener query discovers the top entities to plot.
  const screenerFilters: ScreenerReq = (() => {
    const firstMetric = screenerMetrics[0] ?? 'screener_headcount';
    const base = buildScreenerFilters({
      effectiveFilters,
      primaryDimension,
      metric: firstMetric,
      pagination: { limit: discoveryLimit, offset: 0 },
      sorting: {
        sort_by: ScreenerSortCol.SortByHeadcount,
        ascending: false,
        num_col: 1,
      },
    });

    const headcountSegments = base.segments?.headcount;
    const updatedBase =
      Array.isArray(headcountSegments) && headcountSegments.length > 0
        ? {
            ...base,
            segments: {
              ...base.segments,
              salary: headcountSegments.map((seg) => ({
                ...seg,
                start_val: 0,
              })),
            },
          }
        : base;

    return {
      ...updatedBase,
      entity_info_selectors: {
        ...updatedBase.entity_info_selectors,
        company_info_selectors: {
          ...updatedBase.entity_info_selectors?.company_info_selectors,
          is_subsidiary: false,
        },
      },
    };
  })();

  const [{ data: screenerData, status: screenerStatus }] =
    useUrqlQueryWithStatus<
      { screener: { entities: ScreenerRespEntity[] } },
      { filters: ScreenerReq }
    >({
      query: GET_SCREENER_DATA,
      variables: { filters: screenerFilters },
      pause: !screenerFilters.dim,
    });

  const entities = screenerData?.screener?.entities ?? [];
  const companyIds = entities.map((e) => e.entity_id);
  const hasEntities = companyIds.length > 0;

  const shouldFetchSentiment = sentimentMetrics.length > 0 && hasEntities;

  const [{ data: sentimentData, status: sentimentStatus }] =
    useUrqlQueryWithStatus<
      GetSentimentDataQuery,
      GetSentimentDataQueryVariables
    >({
      query: SENTIMENT_GET_OVERTIME_DATA_V2,
      variables: {
        dim1: Dimension1.Company,
        filters: { company: companyIds, ...secondaryFilters },
      },
      pause: !shouldFetchSentiment,
    });

  // Snowflake query supplies sentiment rating + prestige + core columns.
  const {
    data: snowflakeRows,
    status: snowflakeStatus,
    enabled: snowflakeEnabled,
  } = useSnowflakeSavedQuery(snowflakeConfig);

  // When Snowflake data is available, use its rcids for the postings lookup.
  const snowflakeRcids = (snowflakeRows ?? [])
    .map((r) => r['rcid'])
    .filter((id): id is number => typeof id === 'number');
  const postingsCompanyIds =
    snowflakeConfig && snowflakeRcids.length > 0 ? snowflakeRcids : companyIds;
  const hasPostingsCompanies = postingsCompanyIds.length > 0;
  const shouldFetchPostings = postingMetrics.length > 0 && hasPostingsCompanies;

  const [{ data: postingsDataV2, status: postingsStatusV2 }] =
    useUrqlQueryWithStatus<
      PostingsGetActiveQuery,
      PostingsGetActiveQueryVariables
    >({
      query: POSTINGS_GET_ACTIVE,
      variables: {
        dim1: Dimension1.Company,
        filters: {
          company: postingsCompanyIds,
          provider: [POSTING_SOURCE_IDS.unified],
          ...secondaryFilters,
        },
      },
      pause: isPostingsV3Enabled || !shouldFetchPostings,
    });

  // The company ids to plot come from the screener/Snowflake, not the primary
  // filter, so build from an explicit entity list. Each response entity is keyed
  // by `entity.index`, zipped back to these request entities to recover the
  // company id. NORMAL mode (default) returns counts on `metrics.active`,
  // matching the legacy active-only request.
  const postingsV3Entities: PostingsV3Ent[] = postingsCompanyIds.map(
    (EntID) => ({ EntID, EntType: EntKind.Company })
  );
  const postingsV3Variables = buildPostingsV3VariablesFromEntities({
    entities: postingsV3Entities,
    effectiveFilters,
  });

  const {
    weekRefs,
    isLoading: isWeekRefsLoading,
    error: weekRefsError,
  } = usePostingsV3WeekRefs({ pause: !isPostingsV3Enabled });

  const [{ data: postingsDataV3, status: postingsStatusV3 }] =
    useUrqlQueryWithStatus<PostingsV3DataQuery>({
      query: POSTINGS_V3_GET_DATA,
      variables: postingsV3Variables,
      pause: !isPostingsV3Enabled || !shouldFetchPostings || isWeekRefsLoading,
    });

  const postingsData: PostingsGetActiveQuery = isPostingsV3Enabled
    ? {
        posting: convertPostingsV3ToActivePostings({
          data: postingsDataV3,
          requestEntities: postingsV3Entities,
          weekRefs,
        }),
      }
    : (postingsDataV2 ?? {});
  const postingsStatus = isPostingsV3Enabled
    ? postingsStatusV3
    : postingsStatusV2;

  // Composition query supplies the market-share denominator.
  const filtersForComposition = compositionEffectiveFilters.filter(
    (f) => f.id !== FilterId.COMPANY
  );
  const compositionVarsV3: CompositionV3DataQueryVariables =
    getCompositionV3Variables({
      primaryDimension: FilterId.ROLE,
      effectiveFilters: filtersForComposition,
      isSnapshot: true,
    });

  const compositionPausedV3 =
    !needsMarketShare ||
    !hasEntities ||
    (Array.isArray(compositionVarsV3.entities) &&
      compositionVarsV3.entities.length === 0) ||
    !compositionVarsV3.filters?.start_date ||
    !compositionVarsV3.filters?.end_date;

  const [{ data: compositionDataUnion, status: compositionStatusUnion }] =
    useUrqlQueryWithStatus<
      CompositionV3DataQuery,
      CompositionV3DataQueryVariables
    >({
      query: COMPOSITION_GET_DATA_V3,
      variables: compositionVarsV3,
      pause: compositionPausedV3,
    });

  const compositionDataForHeadcount = compositionDataUnion;

  const compositionStatusForCombine = compositionStatusUnion;

  const compositionFetchPaused = compositionPausedV3;

  const processedScreener = processMultipleScreenerMetrics(
    entities,
    screenerMetrics
  );
  const headcountByCompany = new Map(
    processMultipleScreenerMetrics(entities, ['screener_headcount'])[
      'screener_headcount'
    ]?.map((p) => [p.dim, p.value]) ?? []
  );
  const totalHeadcount = getTotalHeadcount(compositionDataForHeadcount);

  const sentimentResults = sentimentData?.sentiment ?? [];
  const postingsResults = postingsData?.posting ?? [];

  const getMetricData = (
    m: MetricValues
  ): Array<{ dim: string; value: number }> => {
    if (isScreenerMetric(m)) {
      return processedScreener[m] ?? [];
    }
    if (isSentimentMetric(m)) {
      return extractSentimentMetricValues(sentimentResults, entities, m);
    }
    if (m === 'postings_active') {
      return extractActivePostings(postingsResults, entities);
    }
    if (m === 'market_share') {
      return extractMarketShare(entities, headcountByCompany, totalHeadcount);
    }
    return [];
  };

  const byMetric: MultiMetricProviderResult['byMetric'] = {};

  if (snowflakeConfig && snowflakeRows && snowflakeRows.length > 0) {
    const postingsByRcid = buildPostingsByRcid(postingsResults);
    const { rows, columnMetrics, columnFormats } = buildSnowflakeTableRows(
      snowflakeRows,
      postingsByRcid,
      headcountColumnName
    );

    const targetCompanies = findTargetCompanies(snowflakeRows);
    if (targetCompanies.length > 0) {
      byMetric['target_companies_rec'] = {
        type: DataType.SINGLE,
        data: targetCompanies.map((name, i) => ({
          dim: name,
          value: targetCompanies.length - i,
        })),
      };
    }

    byMetric['market_share'] = {
      type: DataType.SINGLE,
      data: extractSnowflakeMarketShare(snowflakeRows),
    };

    byMetric['screener_headcount'] = {
      type: DataType.TABLE_ROWS,
      data: rows.map((row) => ({
        ...row,
        __columnMetrics: columnMetrics,
        __columnFormats: columnFormats,
      })),
    };
  } else {
    const headcountData = getMetricData('screener_headcount');
    const metricDataByCompany = new Map<string, Record<string, unknown>>();

    for (const item of headcountData) {
      metricDataByCompany.set(item.dim, {
        Company: item.dim,
        [headcountColumnName]: item.value,
      });
    }

    const columnMetrics: Record<string, string> = {
      [headcountColumnName]: 'screener_headcount',
    };

    for (const m of metrics) {
      if (m === 'screener_headcount') continue;
      if (m === 'market_share' && snowflakeConfig) continue;
      const metricData = getMetricData(m);
      const metricLabelMap: Record<string, string> = {
        market_share: 'Market Share',
        screener_salary: 'Salary',
        postings_active: 'Active Postings',
        overall: 'Sentiment Rating',
      };
      const label = metricLabelMap[m] ?? m;
      columnMetrics[label] = m;

      for (const d of metricData) {
        const row = metricDataByCompany.get(d.dim);
        if (row) {
          row[label] = d.value;
        }
      }
    }

    const columnFormats: Record<string, string> = {
      'Market Share': '.2%',
    };

    const rows = Array.from(metricDataByCompany.values());

    byMetric['screener_headcount'] = {
      type: DataType.TABLE_ROWS,
      data: rows.map((row) => ({
        ...row,
        __columnMetrics: columnMetrics,
        __columnFormats: columnFormats,
      })),
    };
  }

  const statuses = [screenerStatus];
  if (shouldFetchSentiment) statuses.push(sentimentStatus);
  if (shouldFetchPostings) {
    // A week-refs failure means no id -> date map, so every active point is
    // dropped — surface it as an error rather than empty postings.
    statuses.push(
      isPostingsV3Enabled && weekRefsError
        ? DataProviderQueryStatus.Error
        : postingsStatus
    );
  }
  if (!compositionFetchPaused) statuses.push(compositionStatusForCombine);
  if (snowflakeEnabled) statuses.push(snowflakeStatus);
  const status = combineQueryStatus(statuses);

  return children({
    data: { byMetric },
    status,
  });
};
